import { useState } from "react";
import { FiDownload } from "react-icons/fi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { downloadPaymentSchedule, type ExportFormat } from "@/features/worksheets/api";
import { formatMoney } from "@/features/worksheets/money";
import type { OfferSummary } from "@/features/worksheets/types";

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b py-2 last:border-b-0">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

export function PaymentScheduleDialog({
  caseId,
  summary,
  open,
  onOpenChange,
}: {
  caseId: string;
  summary: OfferSummary;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [downloading, setDownloading] = useState<ExportFormat | null>(null);

  async function download(fmt: ExportFormat) {
    setDownloading(fmt);
    try {
      await downloadPaymentSchedule(caseId, fmt);
    } catch {
      toast.error("Couldn't download the payment schedule — try again.");
    } finally {
      setDownloading(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Payment Schedule</DialogTitle>
          <DialogDescription>
            Installment-agreement totals. Download the full amortization schedule below.
          </DialogDescription>
        </DialogHeader>
        <div className="grid">
          <SummaryRow label="Monthly payment" value={formatMoney(summary.monthlyPaymentCents)} />
          <SummaryRow label="Months" value={String(summary.calculatedMonths)} />
          <SummaryRow label="Total due" value={formatMoney(summary.totalDueCents)} />
          <SummaryRow label="Interest rate" value={`${summary.interestRate}%`} />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => download("pdf")}
            disabled={downloading !== null}
          >
            <FiDownload className="size-4" /> Download PDF
          </Button>
          <Button type="button" onClick={() => download("xlsx")} disabled={downloading !== null}>
            <FiDownload className="size-4" /> Download Excel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
